import { useState } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useNotify } from 'react-admin';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';
import { axiosInstance } from '../../../services/http';
import { isSameBoardsQuantity } from '../../../utils/boardUtils';
// Define interfaces locally instead of importing from mocks
interface BoardPurchase {
  id: string;
  isPrimary: boolean;
  canceled: boolean;
  poNumber: string;
  mudCount: number;
  boardPurchaseFloors: any[];
}

interface BoardType {
  id: string;
  name: string;
  shortName: string;
  thickness: number;
  width: number;
  length: number;
}

interface BoardReceiptFloor {
  id: string;
  floor: number;
  quantity: number;
  boardTypeId: string;
  boardPurchaseId: string;
}

export interface Job {
  id: string;
  jobNumber: string;
  address: string;
  region: string;
  subdivision: string;
  phase: string;
  status: string;
  contractor: string;
  supervisor: string;
  startDate: string;
  endDate: string;
  boardQuantity: number;
  phaseStructureType?: PhaseStructureTypeEnum;
  job: {
    id: string;
    regionId: string;
    subdivisionId: string;
    currentJobPhasePriority: number;
    builderName: string;
    idNumber: string;
    currentSupplierName: string;
    subdivisionName: string;
    supervisorFullNames: string;
    modelName: string;
    buildingNumber: string;
    lot: string;
    block: string;
    address1: string;
    dateDue: string;
    houseMarked: boolean;
    dateHouseMarked: string | null;
    garageFirst: boolean;
    highCeiling: boolean;
    screwInspection: boolean;
    reqBumpoutTimesheet: boolean;
    options: string;
    hasTrimOptions?: boolean;
    trimOptionsFinished?: boolean;
    interiorOptionsFinished?: boolean;
    exteriorOptionsFinished?: boolean;
    archived?: boolean;
  };
  phases: any[];
  jobPhase: {
    phaseName: string;
    priority: number;
    requested: boolean;
    contractorId: string | null;
    approved: boolean;
  };
  jobPhases: any[];
  boardTypes: BoardType[];
  boardPurchases: BoardPurchase[];
  boardReceiptFloors: BoardReceiptFloor[];
}

interface Filters {
  region?: string;
  subdivision?: string;
  phase?: string[];
  status?: string[];
  search?: string;
}

function useMasterSheet() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobLotError, setJobLotError] = useState(false);

  // Callback for changing tabs (set by the UI component)
  const [changeTabCallback, setChangeTabCallback] = useState<((tab: string) => void) | null>(null);

  // Cache for boards data to avoid reloading
  const [boardsDataCache, setBoardsDataCache] = useState<Record<string, any>>({});

  // Remove mock data initialization - using real data from API now
  // useEffect(() => {
  //   setJobs(mockJobs);
  // }, []);
  const [filters, setFilters] = useState<Filters>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const updateJob = (id: string, data: Partial<Job>) => {
    setJobs(prev =>
      prev.map(job => (job.id === id ? { ...job, ...data } : job)),
    );

    if (selectedJob?.id === id) {
      setSelectedJob(prev => (prev ? { ...prev, ...data } : null));
    }
  };

  const selectJob = (job: Job) => {
    setSelectedJob(job);
  };

  const applyFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filtering
  };

  const changePage = (newPage: number) => {
    setPage(newPage);
  };

  const changePerPage = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // API Integration functions
  const loadJobById = async (jobId: string) => {
    try {
      setLoadingJob(true);
      setJobLotError(false);

      // Load main job data
      const jobResult = await dataProvider.getOne<any>('job-lot-report', {
        id: jobId,
      });

      if (jobResult.data) {
        // Transform API data to our Job interface
        const apiJob = jobResult.data;

        // Load jobPhases data using job-lot-phase-report
        const jobPhases = await loadJobPhases(jobId);

        // Find the current job phase based on priority
        // The current phase is the one with priority matching currentJobPhasePriority
        const currentJobPhase = jobPhases.find(
          (jp: any) => jp.priority === apiJob.currentJobPhasePriority
        ) || {};

        // Structure the data - keep it simple since we have everything in job
        const theJob: any = {
          id: apiJob.id,
          job: apiJob,  // All job data is here
          jobPhases: jobPhases,  // Populated with job-lot-phase-report data
          jobPhase: currentJobPhase,  // Current job phase from jobPhases array
          phases: [],  // Empty - not really used in v2
          boardTypes: [],  // Will be loaded lazily when needed
          boardPurchases: [],  // Will be loaded lazily when needed
          boardReceiptFloors: [],  // Will be loaded lazily when needed
        };

        setSelectedJob(theJob);

        // Update jobs list if this job exists in it
        setJobs(prev =>
          prev.map(job => (job.id === jobId ? theJob : job)),
        );

        return theJob;
      }
    } catch (error) {
      console.error('Error loading job:', error);
      setJobLotError(true);
    } finally {
      setLoadingJob(false);
    }
  };

  // Update job house marked status (specific implementation like V1)
  const updateJobHouseMarked = async (jobId: string, houseMarked: boolean) => {
    try {
      const params = {
        id: jobId,
        data: { houseMarked },
        previousData: selectedJob?.job ? { ...selectedJob.job } : { id: jobId },
      };

      await dataProvider.update('job', params);

      // Update local state
      if (selectedJob) {
        const updatedJob = {
          ...selectedJob,
          job: {
            ...selectedJob.job,
            houseMarked,
          },
        };
        setSelectedJob(updatedJob);

        // Update in jobs list
        setJobs(prev => prev.map(job => (job.id === jobId ? updatedJob : job)));
      }

      const messageMarked = 'House marked!';
      const messageUnMarked = 'House unmarked!';
      notify(houseMarked ? messageMarked : messageUnMarked);
    } catch (error) {
      console.error('Error updating house marked:', error);
      notify(`Error updating house marked: ${error.message}`, 'error');
      throw error;
    }
  };

  // Toggle garage phase required (direct execution like V1)
  const toggleGarageRequired = async (garagePhase: any, currentRequired: boolean) => {
    try {
      const params = {
        id: garagePhase.id,
        data: {
          required: !currentRequired,
        },
        previousData: garagePhase,
      };

      await dataProvider.update('job-phase', params);
      
      // Refresh job phases to get updated data
      await refreshJobPhases();
    } catch (error) {
      console.error('Error toggling garage required:', error);
      notify(`Error toggling garage: ${error.message}`, 'error');
      throw error;
    }
  };

  // Load board purchases for a job
  const loadBoardPurchases = async (jobId: string) => {
    try {
      const result = await dataProvider.getList('board-purchase', {
        filter: { jobId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'ASC' },
      });

      // Transform API data to BoardPurchase interface - keep all fields
      const transformedBoardPurchases = (result.data || []).map((bp: any) => ({
        ...bp, // Keep all original fields including materials array
        isPrimary: bp.isPrimary || false,
        canceled: bp.canceled || false,
        poNumber: bp.poNumber || '',
        mudCount: bp.mudCount || 0,
        boardPurchaseFloors: bp.boardPurchaseFloors || [],
        materials: bp.materials || [], // Explicitly preserve materials array
      }));

      return transformedBoardPurchases;
    } catch (error) {
      console.error('Error loading board purchases:', error);
      notify(`Error loading board purchases: ${error.message}`, 'error');
      return [];
    }
  };

  // Load board receipt floors for a job (load for each board purchase like original)
  const loadBoardReceiptFloors = async (boardPurchases: any[]) => {
    try {
      if (!boardPurchases || boardPurchases.length === 0) {
        return [];
      }

      // Get board purchase IDs
      const boardPurchaseIdList = boardPurchases.map(bp => bp.id);

      // Load board receipt floors for each board purchase (like original)
      const boardReceiptFloorsResult = await Promise.all(
        boardPurchaseIdList.map((boardPurchaseId: any) =>
          dataProvider.getList('board-receipt-floor', {
            filter: { boardPurchaseId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          }),
        ),
      );

      // Flatten all results into single array
      const allBoardReceiptFloors = boardReceiptFloorsResult.reduce(
        (acc: any[], result) => {
          return acc.concat(result.data || []);
        },
        [] as any[],
      );

      // Transform API data to BoardReceiptFloor interface
      const transformedReceiptFloors = allBoardReceiptFloors.map(
        (brf: any) => ({
          id: brf.id,
          floor: brf.floor || 0,
          quantity: brf.quantity || 0,
          boardTypeId: brf.boardTypeId || '',
          boardPurchaseId: brf.boardPurchaseId || '',
        }),
      );

      return transformedReceiptFloors;
    } catch (error) {
      console.error('Error loading board receipt floors:', error);
      notify(`Error loading board receipt floors: ${error.message}`, 'error');
      return [];
    }
  };

  // Load job phases for a job using job-lot-phase-report endpoint
  const loadJobPhases = async (jobId: string) => {
    try {
      const result = await dataProvider.getList('job-lot-phase-report', {
        filter: { jobId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'ASC' },
      });

      return result.data || [];
    } catch (error) {
      console.error('Error loading job phases:', error);
      notify(`Error loading job phases: ${error.message}`, 'error');
      return [];
    }
  };

  // Refresh job phases for the currently selected job (same as v1)
  const refreshJobPhases = async () => {
    if (!selectedJob) {
      return;
    }

    try {
      setJobLotError(false);

      // First, refresh the job data from job-lot-report (same as v1)
      const jobResult = await dataProvider.getOne<any>('job-lot-report', {
        id: selectedJob.id,
      });

      // Then, refresh the job phases
      const updatedJobPhases = await loadJobPhases(selectedJob.id);

      // Find the current job phase based on priority
      const currentJobPhase = updatedJobPhases.find(
        (jp: any) => jp.priority === jobResult.data?.currentJobPhasePriority
      ) || {};

      // Update the selected job with new job data AND new job phases
      setSelectedJob(prev =>
        prev
          ? ({
              ...prev,
              job: {
                ...prev.job,
                ...jobResult.data,
              },
              jobPhases: updatedJobPhases,
              jobPhase: currentJobPhase, // Also update the current job phase
            } as any)
          : null,
      );

    } catch (error) {
      console.error('Error refreshing job data:', error);
      setJobLotError(true);
    }
  };

  // Note: loadPhases removed - not needed as we use job-lot-phase-report which contains all phase info

  const loadBoardTypes = async () => {
    try {
      const result = await dataProvider.getList('board-type', {
        filter: {},
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'sortPriority', order: 'ASC' },
      });

      return result.data || [];
    } catch (error) {
      console.error('Error loading board types:', error);
      notify(`Error loading board types: ${(error as any).message}`, 'error');
      return [];
    }
  };

  // Sign off job report
  const signJobReport = async (
    jobId: string,
    floors: any[],
    boardPurchase: any,
  ) => {
    try {
      const boardReceiptFloors: any[] = [];

      floors.forEach((floor: any) => {
        floor.rows.forEach((row: any) => {
          const keys = Object.keys(row.boards);
          keys.forEach(key => {
            if (row.boards[key].quantity && row.boards[key].quantity > 0) {
              boardReceiptFloors.push({
                floor: floor.floor,
                quantity: row.boards[key].quantity
                  ? row.boards[key].quantity
                  : 0,
                boardTypeId: key,
                boardPurchaseId: boardPurchase.id,
              });
            }
          });
        });
      });

      const payload = {
        boardPurchaseId: boardPurchase.id,
        boardReceiptFloors,
      };

      await axiosInstance.post('/job-signoff/signoff', payload);

      // Clear boards cache and reload job data to reflect changes
      clearBoardsCache(jobId);
      await loadJobById(jobId);

      if (isSameBoardsQuantity(boardPurchase, floors, boardReceiptFloors)) {
        notify('Board Count matches the Purchase Order.');
        if (changeTabCallback) {
          changeTabCallback('timeline');
        }
      } else {
        notify('Board Count does not match the Purchase Order.', 'warning');
      }

      try {
        return await loadBoardsData(jobId, true);
      } catch (reloadError) {
        console.error('Error reloading boards data:', reloadError);
        return undefined;
      }
    } catch (error: any) {
      console.error('Error signing job report:', error);
      // Extract error message from backend response (same as dataProvider)
      const errorMessage =
        (error.response?.data?.message && typeof error.response.data.message === 'string')
          ? error.response.data.message
          : (error.response?.data?.message?.[0]) ||
            (error.response?.statusText) ||
            error.message;
      notify(errorMessage, 'warning');
      throw error;
    }
  };

  // Lazy loading functions for specific sections
  const loadRepairsData = async (jobId: string) => {
    try {
      const result = await dataProvider.getList('job-lot-repair-report', {
        filter: { jobId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'ASC' },
      });
      return result.data || [];
    } catch (error) {
      console.error('Error loading repairs:', error);
      notify(`Error loading repairs: ${(error as any).message}`, 'error');
      return [];
    }
  };

  const loadNotesData = async (jobId: string) => {
    try {
      const result = await dataProvider.getList('job-note', {
        filter: { jobId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
      });
      return result.data || [];
    } catch (error) {
      console.error('Error loading notes:', error);
      notify(`Error loading notes: ${(error as any).message}`, 'error');
      return [];
    }
  };

  const loadTimelineData = async (jobId: string) => {
    try {
      const [jobRepairs, jobNotes] = await Promise.all([
        dataProvider.getList('job-lot-repair-report', {
          filter: { jobId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'DESC' },
        }),
        dataProvider.getList('job-note', {
          filter: { jobId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'DESC' },
        })
      ]);

      return {
        repairs: jobRepairs.data || [],
        notes: jobNotes.data || []
      };
    } catch (error) {
      console.error('Error loading timeline:', error);
      notify(`Error loading timeline: ${(error as any).message}`, 'error');
      return { repairs: [], notes: [] };
    }
  };

  // CRUD operations for repairs
  const createRepair = async (repairData: any) => {
    try {
      const { data: createdRepair } = await dataProvider.create('/job-repair', {
        data: repairData
      });
      notify('Repair created successfully!', 'success');
      return createdRepair;
    } catch (error) {
      console.error('Error creating repair:', error);
      notify(`Error creating repair: ${(error as any).message}`, 'error');
      throw error;
    }
  };

  const updateRepair = async (repairId: string, repairData: any, previousData: any) => {
    try {
      await dataProvider.update('/job-repair', {
        id: repairId,
        data: repairData,
        previousData
      });
      notify('Repair updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating repair:', error);
      notify(`Error updating repair: ${(error as any).message}`, 'error');
      throw error;
    }
  };

  const deleteRepair = async (repairId: string, previousData: any) => {
    try {
      await dataProvider.delete('/job-repair', {
        id: repairId,
        previousData
      });
      notify('Repair deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting repair:', error);
      notify(`Error deleting repair: ${(error as any).message}`, 'error');
      throw error;
    }
  };

  const loadRepairDetails = async (repairId: string) => {
    try {
      const { data } = await dataProvider.getOne('job-repair', { id: repairId });
      return data;
    } catch (error) {
      console.error('Error loading repair details:', error);
      notify(`Error loading repair details: ${(error as any).message}`, 'error');
      throw error;
    }
  };

  const loadBoardsData = async (jobId: string, forceReload = false) => {
    // Check cache first unless force reload is requested
    if (!forceReload && boardsDataCache[jobId]) {
      return boardsDataCache[jobId];
    }

    try {
      const [boardPurchases, boardTypes] = await Promise.all([
        loadBoardPurchases(jobId),
        loadBoardTypes(),
      ]);

      const boardReceiptFloors = await loadBoardReceiptFloors(boardPurchases);

      const data = {
        boardPurchases,
        boardTypes,
        boardReceiptFloors,
      };

      // Cache the data
      setBoardsDataCache(prev => ({
        ...prev,
        [jobId]: data,
      }));

      return data;
    } catch (error) {
      console.error('Error loading boards:', error);
      notify(`Error loading boards: ${(error as any).message}`, 'error');
      return {
        boardPurchases: [],
        boardTypes: [],
        boardReceiptFloors: [],
      };
    }
  };

  // Clear boards cache for a specific job (useful after updates)
  const clearBoardsCache = (jobId: string) => {
    setBoardsDataCache(prev => {
      const newCache = { ...prev };
      delete newCache[jobId];
      return newCache;
    });
  };

  return {
    // State
    jobs,
    selectedJob,
    filters,
    loading,
    loadingJob,
    jobLotError,
    page,
    perPage,

    // Actions
    setJobs,
    setSelectedJob,
    updateJob,
    selectJob,
    applyFilters,
    changePage,
    changePerPage,
    setLoading,

    // API Actions
    loadJobById,
    updateJobHouseMarked,
    toggleGarageRequired,
    loadBoardPurchases,
    loadBoardReceiptFloors,
    loadJobPhases,
    refreshJobPhases,
    signJobReport,

    // Lazy loading functions
    loadRepairsData,
    loadNotesData,
    loadTimelineData,
    loadBoardsData,
    clearBoardsCache,

    // Repair CRUD operations
    createRepair,
    updateRepair,
    deleteRepair,
    loadRepairDetails,

    // Tab management
    setChangeTabCallback,
  };
}

export const MasterSheetContainer = createContainer(useMasterSheet);
